<template>
  <input
    ref="inputRef"
    :value="displayValue"
    :placeholder="placeholder"
    :class="inputClass"
    @input="onInput"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps<{
  modelValue?: string;
  value?: string;
  placeholder?: string;
  inputClass?: string;
  options?: Record<string, unknown>;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "place_changed", place: any): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const autocompleteRef = ref<any>(null);
const placeListenerRef = ref<any>(null);
const retryTimerRef = ref<number | null>(null);
const retryCountRef = ref(0);
const MAX_RETRIES = 15;
const RETRY_DELAY_MS = 300;
let didWarnInitialization = false;

const displayValue = computed(() => props.modelValue ?? props.value ?? "");

const getMapsApi = () => {
  return (globalThis as any)?.google?.maps;
};

const clearRetry = () => {
  if (retryTimerRef.value !== null) {
    window.clearTimeout(retryTimerRef.value);
    retryTimerRef.value = null;
  }
  retryCountRef.value = 0;
};

const setInputValue = (value: string) => {
  if (inputRef.value && inputRef.value.value !== value) {
    inputRef.value.value = value;
  }
};

watch(displayValue, (value) => {
  setInputValue(value ?? "");
});

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const setupAutocomplete = () => {
  if (!inputRef.value) return false;
  const mapsApi = getMapsApi();
  if (!mapsApi?.places?.Autocomplete) return false;

  autocompleteRef.value = new mapsApi.places.Autocomplete(inputRef.value, props.options || {});
  placeListenerRef.value = autocompleteRef.value.addListener("place_changed", () => {
    const place = autocompleteRef.value?.getPlace?.();
    emit("place_changed", place);
  });

  setInputValue(displayValue.value);
  return true;
};

const attemptInitializeAutocomplete = () => {
  if (setupAutocomplete()) {
    clearRetry();
    return;
  }

  if (retryCountRef.value >= MAX_RETRIES) {
    clearRetry();
    if (!didWarnInitialization) {
      didWarnInitialization = true;
      console.warn("[MapAutocompleteInput] Google Places Autocomplete not available after retries.");
    }
    return;
  }

  retryCountRef.value += 1;
  retryTimerRef.value = window.setTimeout(attemptInitializeAutocomplete, RETRY_DELAY_MS);
};

onMounted(() => {
  attemptInitializeAutocomplete();
});

onBeforeUnmount(() => {
  clearRetry();
  const mapsApi = getMapsApi();
  if (placeListenerRef.value && mapsApi?.event) {
    mapsApi.event.removeListener(placeListenerRef.value);
  }
  placeListenerRef.value = null;
  autocompleteRef.value = null;
});
</script>
